import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/LanguageContext';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Building2, Landmark, ShieldCheck, Wallet, ArrowDownRight, Layers } from 'lucide-react';
import type { Member } from '../../../../shared/types';

export const GroupOverview: React.FC = () => {
  const { t } = useTranslation();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  // Group aggregates state
  const [totalExpenses, setTotalExpenses] = useState(125000); // Sample baseline or from settings
  const [totalLandPayments, setTotalLandPayments] = useState(500000);
  const [bankStatementBalance, setBankStatementBalance] = useState(1450000);

  useEffect(() => {
    // Read members collection (allowed by Firestore rules for active members)
    const q = query(collection(db, 'members'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Member[] = [];
        snap.forEach((doc) => list.push(doc.data() as Member));
        // Sort active members first, then by name
        list.sort((a, b) => a.name.localeCompare(b.name));
        setMembers(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Group overview offline or restricted:', err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const totalApprovedSavings = members.reduce((sum, m) => sum + (m.approvedTotal || 0), 0);
  const ledgerDerivedBalance = totalApprovedSavings - totalExpenses - totalLandPayments;
  const discrepancy = bankStatementBalance - ledgerDerivedBalance;

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 sm:py-8 pb-24 sm:pb-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Building2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          <span>{t('group.title')}</span>
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('group.subtitle')}
        </p>
      </div>

      {/* Group Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {/* Total Collected */}
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm">
          <div className="flex items-center justify-between mb-2 text-emerald-600 dark:text-emerald-400">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('group.totalCollected')}
            </span>
            <Wallet className="w-4 h-4" />
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {fmt(totalApprovedSavings)}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">{members.length} participating members</p>
        </div>

        {/* Total Expenses */}
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm">
          <div className="flex items-center justify-between mb-2 text-amber-500">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('group.totalExpenses')}
            </span>
            <ArrowDownRight className="w-4 h-4" />
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {fmt(totalExpenses)}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Operational & documentation</p>
        </div>

        {/* Total Land Payments */}
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm">
          <div className="flex items-center justify-between mb-2 text-primary-500">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('group.totalLandPayments')}
            </span>
            <Layers className="w-4 h-4" />
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {fmt(totalLandPayments)}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Manikganj / Savar parcel</p>
        </div>

        {/* Ledger Derived Balance */}
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-200/80 dark:border-navy-800 shadow-sm">
          <div className="flex items-center justify-between mb-2 text-blue-500">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {t('group.ledgerBalance')}
            </span>
            <Landmark className="w-4 h-4" />
          </div>
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {fmt(ledgerDerivedBalance)}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Bank Statement: {fmt(bankStatementBalance)}
          </p>
        </div>
      </div>

      {/* Member Roster Summary Table */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-navy-800 flex items-center justify-between">
          <h2 className="font-bold text-gray-900 dark:text-white text-base">
            {t('group.memberSummary')}
          </h2>
          <span className="text-xs text-gray-400">
            {members.filter((m) => m.status === 'active').length} Active Members
          </span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-navy-950/40 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">{t('group.member')}</th>
                  <th className="py-3 px-4">{t('profile.membershipStatus')}</th>
                  <th className="py-3 px-4 text-right">{t('group.approvedAmount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-navy-800">
                {members.map((m, idx) => (
                  <tr key={m.uid} className="hover:bg-gray-50/50 dark:hover:bg-navy-800/40">
                    <td className="py-3.5 px-4 text-gray-400 font-mono text-xs">{idx + 1}</td>
                    <td className="py-3.5 px-4 font-semibold text-gray-900 dark:text-white">
                      {m.name}
                    </td>
                    <td className="py-3.5 px-4">
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          m.status === 'active'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                            : 'bg-gray-100 text-gray-700 dark:bg-navy-800 dark:text-gray-400'
                        }`}
                      >
                        {m.status === 'active' ? t('profile.active') : t('profile.exited')}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right font-bold text-gray-900 dark:text-white">
                      {fmt(m.approvedTotal || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Privacy Note */}
        <div className="p-4 bg-gray-50/70 dark:bg-navy-950/40 border-t border-gray-100 dark:border-navy-800 text-xs text-gray-500 dark:text-gray-400 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
          <span>{t('group.privacyNotice')}</span>
        </div>
      </div>
    </div>
  );
};
